import type React from 'react'
import type { TextChain } from '@/api/types/stowed-element'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import { usePolledLinkingFrames } from './hooks/use-polled-linking-frames'
import { TextLinkingLayer, type TextLinkingLayerProps } from './components/text-linking-layer'

export interface TextLinkingOverlayProps extends Omit<
  TextLinkingLayerProps,
  'chains' | 'allTextFrames'
> {
  zIndex: number
  /**
   * Immediate nudge from the parent after a commit or a link/unlink. Kept alongside the poll
   * (`usePolledLinkingFrames`) purely so a topology change paints on the very next render instead
   * of the next frame; the poll is what guarantees correctness.
   */
  revision: number
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>
  /** Wire-shaped chains (uid strings), resolved to live frames by the poll. */
  linkedTextChains: TextChain[]
}

/**
 * Full-canvas container for `TextLinkingLayer`. `pointerEvents: 'none'` is baked in here (not
 * left to call sites) so the overlay can never accidentally intercept clicks/drags meant for the
 * canvas below it — see WMPCM-6852 regression where an equivalent inline wrapper omitted this and
 * broke all shape/text selection.
 *
 * This is also the container/presentational seam: the overlay samples live canvas state, the layer
 * stays a pure function of its props (which is what keeps `text-linking-layer.test.tsx` able to
 * render it with hand-built frames).
 */
export function TextLinkingOverlay({
  zIndex,
  revision,
  shapeManagerRef,
  linkedTextChains,
  ...layerProps
}: TextLinkingOverlayProps): React.JSX.Element {
  const { frames, chains, overflowBadgeFrameIds } = usePolledLinkingFrames(
    shapeManagerRef,
    linkedTextChains
  )

  return (
    <div
      className="absolute inset-0"
      style={{ zIndex, pointerEvents: 'none' }}
      data-text-linking-revision={revision}
    >
      <TextLinkingLayer
        {...layerProps}
        chains={chains}
        allTextFrames={frames}
        overflowBadgeFrameIds={overflowBadgeFrameIds}
      />
    </div>
  )
}

export default TextLinkingOverlay
